<template>
  <div class="bg-surface-grey-light rounded-lg p-[6px]">
    <div
      class="flex border border-input-border rounded-md bg-surface-light overflow-hidden"
    >
      <button
        type="button"
        class="flex items-center justify-center cursor-pointer w-12 h-12 text-text hover:bg-surface-grey transition-colors"
        :disabled="currentValue <= props.min"
        @click="decrease"
        :title="$t('common.decrease')"
      >
        <MinusIcon class="w-5 h-5 fill-surface-dark" />
      </button>

      <input
        :value="currentValue"
        type="text"
        inputmode="numeric"
        :class="[
          'flex-1 text-center border-0 border-b-2  bg-transparent py-2 min-w-0 appearance-none focus:outline-none focus:ring-1 focus:ring-input-border',
          currentValue > 0 ? 'border-primary' : 'border-transparent',
        ]"
        :min="props.min"
        :max="props.max"
        :step="props.step"
        :required="props.required"
        pattern="[0-9]*"
        @input="onInput"
        @blur="onBlur"
      />

      <button
        type="button"
        class="flex items-center justify-center cursor-pointer w-12 h-12 text-text hover:bg-surface-grey transition-colors"
        :disabled="currentValue >= props.max"
        @click="increase"
        :title="$t('common.increase')"
      >
        <PlusIcon class="w-5 h-5 fill-surface-dark" />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, ref } from 'vue';

  import MinusIcon from '@/components/icons/MinusIcon.vue';
  import PlusIcon from '@/components/icons/PlusIcon.vue';

  interface Props {
    modelValue?: number;
    min?: number;
    max?: number;
    step?: number;
    firstStep?: number;
    defaultValue?: number;
    required?: boolean;
  }

  const props = withDefaults(defineProps<Props>(), {
    min: 0,
    max: 100,
    step: 1,
    firstStep: 1,
    defaultValue: 0,
  });

  const emit = defineEmits<{
    'update:modelValue': [value: number];
  }>();

  const internalValue = ref(props.defaultValue);

  const currentValue = computed(() => {
    return props.modelValue !== undefined
      ? props.modelValue
      : internalValue.value;
  });

  function updateValue(newValue: number) {
    if (props.modelValue !== undefined) {
      emit('update:modelValue', newValue);
    } else {
      internalValue.value = newValue;
    }
  }

  function decrease() {
    let newValue = Math.max(props.min, currentValue.value - props.step);
    if(props.firstStep && newValue != props.defaultValue && props.firstStep > newValue) {
      newValue = props.min;
    }
    updateValue(newValue);
  }

  function increase() {
    let newValue = Math.min(props.max, currentValue.value + props.step);
    if(props.firstStep && newValue != props.defaultValue && props.firstStep > newValue) {
      newValue = props.firstStep;
    }
    updateValue(newValue);
  }

  function onInput(event: Event) {
    const target = event.target as HTMLInputElement;
    const value = parseInt(target.value, 10);

    if (!isNaN(value)) {
      const clampedValue = Math.max(props.min, Math.min(props.max, value));
      updateValue(clampedValue);
    }
  }

  function onBlur(event: Event) {
    const target = event.target as HTMLInputElement;
    const value = parseInt(target.value, 10);

    if (isNaN(value)) {
      updateValue(props.min);
    }
  }
</script>
